import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchHomeMembers,
  type HomeMember,
} from "../lib/auth";
import {
  fetchMyHouseholds,
  leaveHousehold,
  removeMember,
  type Household,
} from "../lib/households";
import { countPlantsInHousehold } from "../lib/plants";
import { supabase } from "../lib/supabase";
import {
  BackIcon,
  GearIcon,
  HomeIcon,
  PersonIcon,
} from "../components/icons";

type Status = "PRIMARY" | "MEMBER";

interface DecoratedMember extends HomeMember {
  status: Status;
  isCurrentUser: boolean;
}

export default function HousematesScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [home, setHome] = useState<Household | null>(null);
  const [members, setMembers] = useState<DecoratedMember[]>([]);
  const [plantCount, setPlantCount] = useState(0);
  const [isOwner, setIsOwner] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return;

      const homes = await fetchMyHouseholds();
      const first = homes[0] || null;
      setHome(first);
      setIsOwner(first?.created_by === auth.user.id);

      if (!first) {
        setMembers([]);
        setPlantCount(0);
        return;
      }

      const rawMembers = await fetchHomeMembers(first.id);
      const decorated: DecoratedMember[] = rawMembers.map((m) => ({
        ...m,
        status: m.user_id === first.created_by ? "PRIMARY" : "MEMBER",
        isCurrentUser: m.user_id === auth.user!.id,
      }));
      decorated.sort((a, b) => {
        if (a.isCurrentUser && !b.isCurrentUser) return -1;
        if (!a.isCurrentUser && b.isCurrentUser) return 1;
        return a.joined_at.localeCompare(b.joined_at);
      });
      setMembers(decorated);
      setPlantCount(await countPlantsInHousehold(first.id));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleShareCode() {
    if (!home) return;
    const text = `Join my Sprig home "${home.name}" with code: ${home.code}`;
    if (navigator.share) {
      try {
        await navigator.share({ text });
      } catch {
        // user cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        alert("Invite copied to clipboard");
      } catch {
        alert(text);
      }
    }
  }

  function handleLeave() {
    if (!home) return;
    if (
      confirm(
        `Leave "${home.name}"? You'll no longer see its plants or get reminders.`
      )
    ) {
      leaveHousehold(home.id)
        .then(() => navigate("/"))
        .catch((err) =>
          alert(err instanceof Error ? err.message : "Could not leave.")
        );
    }
  }

  function handleRemoveMember(mate: DecoratedMember) {
    if (!home) return;
    if (
      confirm(
        `Remove ${mate.display_name || "this member"}? They'll lose access to this household.`
      )
    ) {
      removeMember(home.id, mate.user_id)
        .then(() => load())
        .catch((err) =>
          alert(err instanceof Error ? err.message : "Could not remove.")
        );
    }
  }

  if (loading) return <div className="loading-screen">Loading…</div>;

  return (
    <div className="app-shell">
      <div className="header-row" style={{ paddingTop: 64 }}>
        <button type="button" className="icon-btn" onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <HomeIcon size={20} color="#1f5a39" />
          <h1 className="header-title">{home?.name || "Household"}</h1>
        </div>
        <button
          type="button"
          className="icon-btn"
          onClick={() => navigate("/settings")}
        >
          <GearIcon />
        </button>
      </div>

      <div className="hm-title-block">
        <div className="hm-title">Housemates</div>
        <div className="hm-subtitle">
          {plantCount} {plantCount === 1 ? "plant" : "plants"} tended by{" "}
          {members.length} {members.length === 1 ? "gardener" : "gardeners"}.
        </div>
      </div>

      <div className="hm-cards">
        {members.map((m) => (
          <div key={m.user_id} className="card hm-card">
            <div className="hm-avatar-wrap">
              {m.avatar_url ? (
                <img src={m.avatar_url} alt="" className="hm-avatar" />
              ) : (
                <div
                  className="hm-avatar"
                  style={{
                    background: m.isCurrentUser ? "#2D7A4F" : "#c8e2d2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PersonIcon
                    size={26}
                    color={m.isCurrentUser ? "#fff" : "#2D7A4F"}
                  />
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div className="hm-top-row">
                <div className="hm-name-row">
                  <span className="hm-name">
                    {m.display_name || "Unknown"}
                  </span>
                  {m.isCurrentUser && (
                    <span className="hm-you-badge">YOU</span>
                  )}
                </div>
                {isOwner && !m.isCurrentUser ? (
                  <button
                    type="button"
                    className="hm-remove-btn"
                    onClick={() => handleRemoveMember(m)}
                  >
                    Remove
                  </button>
                ) : (
                  <span className="hm-status">{m.status}</span>
                )}
              </div>
              {m.phone_number && (
                <div className="hm-phone">{m.phone_number}</div>
              )}
            </div>
          </div>
        ))}
        {members.length === 0 && (
          <div className="hm-empty">No members yet.</div>
        )}
      </div>

      {home && (
        <button
          type="button"
          className="danger-btn hm-leave-btn"
          onClick={handleLeave}
        >
          Leave household
        </button>
      )}

      {home && (
        <div className="hm-add-card">
          <div className="hm-add-title">Add a gardener</div>
          <div className="hm-add-sub">
            Share this code with your housemates to grow your collection together.
          </div>
          <div className="hm-code-wrap">
            <div className="hm-code-inner">
              <div className="hm-code-label">HOUSEHOLD CODE</div>
              <div className="hm-code">{home.code}</div>
            </div>
            <button
              type="button"
              className="hm-share-btn"
              onClick={handleShareCode}
              aria-label="Share code"
            >
              📋
            </button>
          </div>
        </div>
      )}

      <style>{`
        .hm-title-block { margin-top: 8px; margin-bottom: 20px; }
        .hm-title {
          font-family: Georgia, serif;
          font-size: 28px;
          font-weight: 700;
          color: #1f5a39;
        }
        .hm-subtitle { font-size: 13px; color: #6b7a6e; margin-top: 6px; }
        .hm-cards { display: flex; flex-direction: column; gap: 12px; }
        .hm-card {
          display: flex;
          align-items: flex-start;
          gap: 14px;
        }
        .hm-avatar-wrap { position: relative; }
        .hm-avatar {
          width: 52px;
          height: 52px;
          border-radius: 26px;
          object-fit: cover;
        }
        .hm-top-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .hm-name-row { display: flex; align-items: center; gap: 8px; }
        .hm-name { font-size: 15px; font-weight: 700; color: #1a2e22; }
        .hm-you-badge {
          background: #2D7A4F;
          color: #fff;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.6px;
          padding: 2px 8px;
          border-radius: 999px;
        }
        .hm-status {
          font-size: 10px;
          font-weight: 800;
          color: #6b7a6e;
          letter-spacing: 0.8px;
        }
        .hm-phone { font-size: 12px; color: #6b7a6e; margin-top: 6px; }
        .hm-empty {
          text-align: center;
          color: #6b7a6e;
          font-size: 13px;
          padding: 24px 0;
        }
        .hm-add-card {
          background: #2D7A4F;
          border-radius: 20px;
          padding: 20px;
          color: #fff;
          margin-top: 24px;
        }
        .hm-add-title {
          font-size: 18px;
          font-weight: 700;
          color: #fff;
        }
        .hm-add-sub {
          color: #cfe8d9;
          font-size: 13px;
          line-height: 18px;
          margin: 6px 0 14px 0;
        }
        .hm-code-wrap {
          display: flex;
          gap: 10px;
          align-items: stretch;
        }
        .hm-code-inner {
          flex: 1;
          border: 1.5px solid #4f9772;
          border-radius: 14px;
          padding: 8px 14px;
        }
        .hm-code-label {
          color: #a8d4bc;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.8px;
        }
        .hm-code {
          color: #fff;
          font-size: 18px;
          font-weight: 700;
          letter-spacing: 3px;
          margin-top: 2px;
        }
        .hm-share-btn {
          width: 52px;
          border-radius: 14px;
          background: #4f9772;
          border: none;
          color: #fff;
          font-size: 18px;
        }
        .hm-remove-btn {
          background: none;
          border: none;
          padding: 0;
          font-size: 12px;
          font-weight: 700;
          color: #c64545;
          cursor: pointer;
        }
        .hm-leave-btn {
          margin-top: 16px;
          margin-bottom: 8px;
        }
      `}</style>
    </div>
  );
}
